import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronUpIcon, FunnelIcon, XMarkIcon, CameraIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import apiClient, { getErrorMessage } from '../../utils/api';
import { GoatMaster, GoatCategory, GoatStatus, TypeOfBirth } from '../../types/api';
import { compressImage, validateImageFile } from '../../utils/imageCompression';

const GoatMasterPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const [goats, setGoats] = useState<GoatMaster[]>([]);
  const [allGoats, setAllGoats] = useState<GoatMaster[]>([]); // Store all goats for client-side filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState<GoatCategory | ''>('');
  const [filterStatus, setFilterStatus] = useState<GoatStatus | ''>('active'); // Default to 'active'
  const [filterBreed, setFilterBreed] = useState<string>('');
  const [filterFatherTag, setFilterFatherTag] = useState<string>('');
  const [filterMotherTag, setFilterMotherTag] = useState<string>('');
  const [filterTOB, setFilterTOB] = useState<TypeOfBirth | ''>('');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [showDeathModal, setShowDeathModal] = useState(false);
  const [deathFormData, setDeathFormData] = useState<{
    goatId: string | null;
    tagNo: string;
    dead_date: string;
    reason_of_death: string;
  }>({
    goatId: null,
    tagNo: '',
    dead_date: '',
    reason_of_death: '',
  });
  // Image state
  const [goatImages, setGoatImages] = useState<Map<string, string>>(new Map());
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set());
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState<string | null>(null);
  // Image upload modal state (for adding images only)
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [uploadingGoatId, setUploadingGoatId] = useState<string | null>(null);
  const [uploadingGoatTag, setUploadingGoatTag] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    // Clear any previous errors when location or business type changes
    setError('');
    
    if (selectedLocation?.id && businessType === 'goat') {
      loadGoats();
    } else {
      setGoats([]);
      setLoading(false);
    }
  }, [selectedLocation?.id, businessType, filterCategory, filterStatus, filterBreed, filterFatherTag, filterMotherTag, filterTOB]);

  const loadGoats = async () => {
    if (!selectedLocation?.id) {
      setError('');
      setGoats([]);
      setAllGoats([]);
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams({ location_id: selectedLocation.id });
      // Don't send 'kid' category to backend - we'll filter client-side to include K-prefixed tags
      if (filterCategory && filterCategory !== 'kid') {
        params.append('category', filterCategory);
      }
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await apiClient.get(`/goat/master?${params.toString()}`);
      const loadedGoats = response.data || [];
      setAllGoats(loadedGoats);
      
      // Apply client-side filters
      let filtered = loadedGoats;
      
      // Special handling for 'kid' category filter: show category='kid' OR tag starting with 'K'
      if (filterCategory === 'kid') {
        filtered = filtered.filter((goat: GoatMaster) => 
          goat.category === 'kid' || goat.tag_no.toUpperCase().startsWith('K')
        );
      } else if (filterCategory) {
        // For other categories, filter normally
        filtered = filtered.filter((goat: GoatMaster) => goat.category === filterCategory);
      }
      
      if (filterBreed) {
        filtered = filtered.filter((goat: GoatMaster) => goat.breed === filterBreed);
      }
      
      if (filterFatherTag) {
        filtered = filtered.filter((goat: GoatMaster) => goat.father_tag === filterFatherTag);
      }
      
      if (filterMotherTag) {
        filtered = filtered.filter((goat: GoatMaster) => goat.mother_tag === filterMotherTag);
      }
      
      if (filterTOB) {
        filtered = filtered.filter((goat: GoatMaster) => goat.last_tob === filterTOB);
      }
      
      setGoats(filtered);
      
      // Load images for all goats
      loadGoatImages(loadedGoats.map((g: GoatMaster) => g.id));
    } catch (err: any) {
      const errorMsg = getErrorMessage(err, 'Failed to load goat records');
      // Only show error if it's not a validation error (which happens when location_id is missing)
      if (!errorMsg.includes('Field required') && !errorMsg.includes('location_id')) {
        setError(errorMsg);
      } else {
        setError('');
        setGoats([]);
        setAllGoats([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadGoatImages = async (goatIds: string[]) => {
    // Load images for all goats in parallel
    const imagePromises = goatIds.map(async (goatId) => {
      // Skip if already loading or already loaded
      if (loadingImages.has(goatId) || goatImages.has(goatId)) {
        return;
      }
      
      setLoadingImages(prev => new Set(prev).add(goatId));
      
      try {
        const response = await apiClient.get(`/goat/master/${goatId}/image`);
        setGoatImages(prev => {
          const newMap = new Map(prev);
          newMap.set(goatId, response.data.image);
          return newMap;
        });
      } catch (err: any) {
        // Image doesn't exist, that's okay - don't show error
        if (err.response?.status !== 404) {
          console.error(`Failed to load image for goat ${goatId}:`, err);
        }
      } finally {
        setLoadingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(goatId);
          return newSet;
        });
      }
    });
    
    await Promise.all(imagePromises);
  };

  const openImageModal = (imageSrc: string) => {
    setModalImageSrc(imageSrc);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setModalImageSrc(null);
  };

  const handleImageClick = (goatId: string, goatImage: string | undefined, tagNo: string) => {
    if (goatImage) {
      // If image exists, open the image modal to view
      openImageModal(goatImage);
    } else {
      // If image doesn't exist, open upload modal to add image
      setUploadingGoatId(goatId);
      setUploadingGoatTag(tagNo);
      setImagePreview(null);
      setShowImageUploadModal(true);
    }
  };

  const closeImageUploadModal = () => {
    setShowImageUploadModal(false);
    setUploadingGoatId(null);
    setUploadingGoatTag('');
    setImagePreview(null);
    setError('');
    // Reset file input
    const fileInput = document.getElementById('goat-image-upload-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    try {
      setError('');
      // Compress image
      const compressedBase64 = await compressImage(file);
      setImagePreview(compressedBase64);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to process image'));
    }
  };

  const handleImageUpload = async () => {
    if (!uploadingGoatId || !imagePreview) {
      setError('Please select an image first');
      return;
    }

    try {
      setUploadingImage(true);
      setError('');
      
      await apiClient.post(`/goat/master/${uploadingGoatId}/image`, {
        goat_id: uploadingGoatId,
        image: imagePreview,
      });

      // Refresh the image in the listing
      await loadGoatImages([uploadingGoatId]);
      
      // Close modal
      closeImageUploadModal();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to upload image'));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImagePreview = () => {
    setImagePreview(null);
    // Reset file input
    const fileInput = document.getElementById('goat-image-upload-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const handleDelete = (goatId: string, tagNo: string) => {
    if (user?.role !== 'admin') {
      setError('Only admins can delete goat records');
      return;
    }

    // Show death information modal
    setDeathFormData({
      goatId,
      tagNo,
      dead_date: getTodayDate(),
      reason_of_death: '',
    });
    setShowDeathModal(true);
  };

  const handleDeathModalClose = () => {
    setShowDeathModal(false);
    setDeathFormData({
      goatId: null,
      tagNo: '',
      dead_date: '',
      reason_of_death: '',
    });
  };

  const handleDeathModalSubmit = async () => {
    if (!deathFormData.goatId) return;

    // Validate required fields
    if (!deathFormData.dead_date) {
      setError('Death date is required');
      return;
    }

    if (!deathFormData.reason_of_death.trim()) {
      setError('Reason of death is required');
      return;
    }

    try {
      setDeletingId(deathFormData.goatId);
      setError('');
      
      // Build query parameters
      const params = new URLSearchParams({
        dead_date: deathFormData.dead_date,
        reason_of_death: deathFormData.reason_of_death.trim(),
      });

      await apiClient.delete(`/goat/master/${deathFormData.goatId}?${params.toString()}`);
      await loadGoats();
      handleDeathModalClose();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to delete goat record'));
    } finally {
      setDeletingId(null);
    }
  };

  const getCategoryColor = (category: GoatCategory) => {
    switch (category) {
      case 'doe': return 'bg-pink-100 text-pink-800';
      case 'buck': return 'bg-blue-100 text-blue-800';
      case 'kid': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: GoatStatus) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'sold': return 'bg-yellow-100 text-yellow-800';
      case 'dead': return 'bg-red-100 text-red-800';
      case 'transferred': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateAge = (goat: GoatMaster): string => {
    // First priority: Calculate from Date of Birth (most accurate)
    if (goat.dob) {
      const dob = new Date(goat.dob);
      if (isNaN(dob.getTime())) return 'Invalid DOB';
      
      const today = new Date();
      
      // Calculate age properly accounting for whether birthday has occurred
      let years = today.getFullYear() - dob.getFullYear();
      let months = today.getMonth() - dob.getMonth();
      let days = today.getDate() - dob.getDate();
      
      // Adjust if birthday hasn't occurred this year
      if (days < 0) {
        months--;
        // Get days in the previous month
        const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        days += prevMonth.getDate();
      }
      
      // Adjust if birthday month hasn't occurred this year
      if (months < 0) {
        years--;
        months += 12;
      }
      
      // Handle negative age (future date)
      if (years < 0) {
        return 'Invalid DOB (future date)';
      }
      
      if (years > 0) {
        return `${years} year${years > 1 ? 's' : ''}`;
      } else if (months > 0) {
        return `${months} month${months > 1 ? 's' : ''}`;
      } else {
        return `${days} day${days > 1 ? 's' : ''}`;
      }
    }

    // Second priority: Calculate from age_months (age at purchase) + months since purchase
    if (goat.age_months !== undefined && goat.age_months !== null && goat.purchase_date) {
      const purchaseDate = new Date(goat.purchase_date);
      if (isNaN(purchaseDate.getTime())) return 'Invalid purchase date';
      
      const today = new Date();
      
      // Calculate months since purchase properly
      let yearsSincePurchase = today.getFullYear() - purchaseDate.getFullYear();
      let monthsSincePurchase = today.getMonth() - purchaseDate.getMonth();
      let daysSincePurchase = today.getDate() - purchaseDate.getDate();
      
      // Adjust if purchase date day hasn't occurred this month
      if (daysSincePurchase < 0) {
        monthsSincePurchase--;
        // Get days in the previous month
        const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        daysSincePurchase += prevMonth.getDate();
      }
      
      // Adjust if purchase date month hasn't occurred this year
      if (monthsSincePurchase < 0) {
        yearsSincePurchase--;
        monthsSincePurchase += 12;
      }
      
      // Handle negative time since purchase (future purchase date)
      if (yearsSincePurchase < 0 || (yearsSincePurchase === 0 && monthsSincePurchase < 0)) {
        return 'Invalid purchase date (future date)';
      }
      
      // Calculate total age in months
      const totalMonthsSincePurchase = yearsSincePurchase * 12 + monthsSincePurchase;
      const currentAgeMonths = goat.age_months + totalMonthsSincePurchase;
      
      // Handle negative age
      if (currentAgeMonths < 0) {
        return 'Invalid age data';
      }
      
      // Format the age
      if (currentAgeMonths < 12) {
        return `${currentAgeMonths} month${currentAgeMonths !== 1 ? 's' : ''} (estimated)`;
      } else {
        const years = Math.floor(currentAgeMonths / 12);
        const months = currentAgeMonths % 12;
        if (months === 0) {
          return `${years} year${years !== 1 ? 's' : ''} (estimated)`;
        }
        return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''} (estimated)`;
      }
    }
    
    return '-';
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatTypeOfBirth = (tob?: TypeOfBirth): string => {
    if (!tob) return '-';
    return tob.charAt(0).toUpperCase() + tob.slice(1);
  };

  const formatCurrency = (amount?: number): string => {
    if (amount === null || amount === undefined || amount === 0) return '-';
    return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getRowBackgroundColor = (status: GoatStatus): string => {
    switch (status) {
      case 'active':
        return 'bg-green-50 hover:bg-green-100';
      case 'sold':
        return 'bg-yellow-50 hover:bg-yellow-100';
      case 'dead':
        return 'bg-red-50 hover:bg-red-100';
      default:
        return 'hover:bg-gray-50';
    }
  };

  const getStickyCellBgColor = (status: GoatStatus): string => {
    switch (status) {
      case 'active':
        return 'bg-green-50';
      case 'sold':
        return 'bg-yellow-50';
      case 'dead':
        return 'bg-red-50';
      default:
        return 'bg-white';
    }
  };

  // Get unique values for filters
  const uniqueBreeds = Array.from(new Set(allGoats.map(g => g.breed).filter(Boolean))).sort();
  const uniqueFatherTags = Array.from(new Set(allGoats.map(g => g.father_tag).filter(Boolean))).sort();
  const uniqueMotherTags = Array.from(new Set(allGoats.map(g => g.mother_tag).filter(Boolean))).sort();
  const uniqueTOBs: TypeOfBirth[] = Array.from(new Set(allGoats.map(g => g.last_tob).filter(Boolean))) as TypeOfBirth[];

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for goat farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">Go to Location Selection</Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/goat/logs" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">🐐 Master Goat Data</h1>
            <p className="text-gray-600">{selectedLocation.name}</p>
          </div>
        </div>
        <Link
          to="/goat/master/new"
          className="btn-touch bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Goat</span>
        </Link>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Death Information Modal */}
      {showDeathModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-bold text-gray-900">
              Mark Goat as Dead
            </h2>
            <p className="text-gray-600">
              Please provide death information for <strong>{deathFormData.tagNo}</strong>
            </p>

            <div className="space-y-4">
              <div>
                <label className="form-label">Death Date *</label>
                <input
                  type="date"
                  value={deathFormData.dead_date}
                  onChange={(e) =>
                    setDeathFormData({ ...deathFormData, dead_date: e.target.value })
                  }
                  className="form-input"
                  max={getTodayDate()}
                />
              </div>

              <div>
                <label className="form-label">Reason of Death *</label>
                <textarea
                  value={deathFormData.reason_of_death}
                  onChange={(e) =>
                    setDeathFormData({ ...deathFormData, reason_of_death: e.target.value })
                  }
                  className="form-input"
                  rows={3}
                  placeholder="Enter reason of death (e.g., Disease, Accident, Old age, etc.)"
                />
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleDeathModalSubmit}
                disabled={deletingId !== null || !deathFormData.dead_date || !deathFormData.reason_of_death.trim()}
                className="flex-1 btn-touch bg-red-600 text-white py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center"
              >
                {deletingId ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Processing...</span>
                  </>
                ) : (
                  'Mark as Dead'
                )}
              </button>
              <button
                onClick={handleDeathModalClose}
                disabled={deletingId !== null}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Collapsible */}
      <div className="card p-0 overflow-hidden">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-gray-600" />
            <span className="font-semibold text-gray-900">Filters</span>
            {(filterCategory || filterStatus !== 'active' || filterBreed || filterFatherTag || filterMotherTag || filterTOB) && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                Active
              </span>
            )}
          </div>
          {showFilters ? (
            <ChevronUpIcon className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-gray-600" />
          )}
        </button>
        
        {showFilters && (
          <div className="px-4 pb-4 pt-2 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Filter by Category</label>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value as GoatCategory | '')}
                  className="form-input"
                >
                  <option value="">All Categories</option>
                  <option value="doe">Doe</option>
                  <option value="buck">Buck</option>
                  <option value="kid">Kid</option>
                </select>
              </div>
              <div>
                <label className="form-label">Filter by Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value as GoatStatus | '')}
                  className="form-input"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="sold">Sold</option>
                  <option value="dead">Dead</option>
                  <option value="transferred">Transferred</option>
                </select>
              </div>
              <div>
                <label className="form-label">Filter by Breed</label>
                <select
                  value={filterBreed}
                  onChange={(e) => setFilterBreed(e.target.value)}
                  className="form-input"
                >
                  <option value="">All Breeds</option>
                  {uniqueBreeds.map(breed => (
                    <option key={breed} value={breed}>{breed}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Filter by Father Tag</label>
                <select
                  value={filterFatherTag}
                  onChange={(e) => setFilterFatherTag(e.target.value)}
                  className="form-input"
                >
                  <option value="">All Fathers</option>
                  {uniqueFatherTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Filter by Mother Tag</label>
                <select
                  value={filterMotherTag}
                  onChange={(e) => setFilterMotherTag(e.target.value)}
                  className="form-input"
                >
                  <option value="">All Mothers</option>
                  {uniqueMotherTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="form-label">Filter by TOB (Type of Birth)</label>
                <select
                  value={filterTOB}
                  onChange={(e) => setFilterTOB(e.target.value as TypeOfBirth | '')}
                  className="form-input"
                >
                  <option value="">All TOB</option>
                  {uniqueTOBs.map(tob => (
                    <option key={tob} value={tob}>{formatTypeOfBirth(tob)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Goats List */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Goat Records ({goats.length})
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : goats.length > 0 ? (
          <>
            {/* Mobile Card View */}
            <div className="block md:hidden space-y-4">
              {goats.map((goat) => {
                const goatImage = goatImages.get(goat.id);
                return (
                <div key={goat.id} className={`border border-gray-200 rounded-lg p-4 space-y-3 ${getRowBackgroundColor(goat.status)}`}>
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {/* Goat Image */}
                      {goatImage ? (
                        <button
                          type="button"
                          onClick={() => openImageModal(goatImage)}
                          className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-200 hover:border-green-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex-shrink-0"
                          title="View image"
                        >
                          <img
                            src={goatImage}
                            alt={`Goat ${goat.tag_no}`}
                            className="w-full h-full object-cover"
                          />
                        </button>
                      ) : loadingImages.has(goat.id) ? (
                        <div className="w-16 h-16 rounded-full border-2 border-gray-200 flex items-center justify-center flex-shrink-0">
                          <LoadingSpinner size="sm" />
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleImageClick(goat.id, undefined, goat.tag_no)}
                          className="w-16 h-16 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 flex-shrink-0"
                          title="Add image"
                        >
                          <span className="text-2xl">🐐</span>
                        </button>
                      )}
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{goat.tag_no}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(goat.category)}`}>
                            {goat.category}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(goat.status)}`}>
                            {goat.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    {user?.role === 'admin' && (
                      <div className="flex items-center space-x-2">
                        <Link
                          to={`/goat/master/${goat.id}/edit`}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </Link>
                        <button
                          onClick={() => handleDelete(goat.id, goat.tag_no)}
                          disabled={deletingId === goat.id}
                          className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingId === goat.id ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <TrashIcon className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-gray-500">Breed:</span>
                      <span className="ml-2 text-gray-900">{goat.breed || '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Age:</span>
                      <span className="ml-2 text-gray-900">{calculateAge(goat)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">DOB:</span>
                      <span className="ml-2 text-gray-900">{goat.dob ? formatDate(goat.dob) : '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Purchase:</span>
                      <span className="ml-2 text-gray-900">{goat.purchase_date ? formatDate(goat.purchase_date) : '-'}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Purchase Amount:</span>
                      <span className="ml-2 text-gray-900 font-medium text-blue-600">
                        {formatCurrency(goat.purchase_amount)}
                      </span>
                    </div>
                    {goat.mother_tag && (
                      <div>
                        <span className="text-gray-500">Mother:</span>
                        <span className="ml-2 text-gray-900 font-medium">{goat.mother_tag}</span>
                      </div>
                    )}
                    {goat.father_tag && (
                      <div>
                        <span className="text-gray-500">Father:</span>
                        <span className="ml-2 text-gray-900 font-medium">{goat.father_tag}</span>
                      </div>
                    )}
                  </div>

                  {/* Weight & Health */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Latest Weight:</span>
                        <span className="ml-2 text-gray-900">
                          {goat.latest_weight_kg ? `${goat.latest_weight_kg} kg` : '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Weight Date:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.latest_weight_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Deworming:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.last_deworming)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Vaccination:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.last_vaccination)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Breeding & Kidding */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Last Breeding:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.last_breeding_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Kidding:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.last_kidding_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last TOB:</span>
                        <span className="ml-2 text-gray-900">{formatTypeOfBirth(goat.last_tob)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Total Kiddings:</span>
                        <span className="ml-2 text-gray-900">{goat.total_kidding_count ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Sale/Death Info */}
                  <div className="pt-2 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500">Sale Date:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.sale_date)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Sale Amount:</span>
                        <span className="ml-2 text-gray-900 font-medium text-green-600">
                          {goat.sale_amount ? `₹${goat.sale_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">Dead Date:</span>
                        <span className="ml-2 text-gray-900">{formatDate(goat.dead_date)}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Reason of Death:</span>
                        <span className="ml-2 text-gray-900">{goat.reason_of_death || '-'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-0 bg-gray-50 z-10">Image</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky left-[4.5rem] bg-gray-50 z-10">Tag No</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Breed</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mother</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Father</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">DOB/Purchase</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Purchase Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sale Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sale Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dead Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason of Death</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Latest Weight</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Weight Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Deworming</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Vaccination</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Breeding</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Kidding</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last TOB</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total Kiddings</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase sticky right-0 bg-gray-50 z-10">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {goats.map((goat) => {
                    const goatImage = goatImages.get(goat.id);
                    return (
                    <tr key={goat.id} className={getRowBackgroundColor(goat.status)}>
                      <td className={`px-3 py-2 text-sm sticky left-0 z-10 ${getStickyCellBgColor(goat.status)}`}>
                        {goatImage ? (
                          <button
                            type="button"
                            onClick={() => openImageModal(goatImage)}
                            className="w-12 h-12 rounded-full overflow-hidden border-2 border-gray-200 hover:border-green-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                            title="View image"
                          >
                            <img
                              src={goatImage}
                              alt={`Goat ${goat.tag_no}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ) : loadingImages.has(goat.id) ? (
                          <div className="w-12 h-12 rounded-full border-2 border-gray-200 flex items-center justify-center">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleImageClick(goat.id, undefined, goat.tag_no)}
                            className="w-12 h-12 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 hover:bg-gray-100 hover:border-green-400 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                            title="Add image"
                          >
                            <span className="text-lg">🐐</span>
                          </button>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-sm font-medium text-gray-900 sticky left-[4.5rem] z-10 ${getStickyCellBgColor(goat.status)}`}>{goat.tag_no}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(goat.category)}`}>
                          {goat.category}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{calculateAge(goat)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{goat.breed || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 font-medium">{goat.mother_tag || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 font-medium">{goat.father_tag || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {goat.dob ? `D-${formatDate(goat.dob)}` : 
                         goat.purchase_date ? `P-${formatDate(goat.purchase_date)}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600 font-medium text-blue-600">
                        {formatCurrency(goat.purchase_amount)}
                      </td>
                      <td className="px-3 py-2 text-sm">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(goat.status)}`}>
                          {goat.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.sale_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600 font-medium text-green-600">
                        {goat.sale_amount ? `₹${goat.sale_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.dead_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{goat.reason_of_death || '-'}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">
                        {goat.latest_weight_kg ? `${goat.latest_weight_kg} kg` : '-'}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.latest_weight_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.last_deworming)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.last_vaccination)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.last_breeding_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatDate(goat.last_kidding_date)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{formatTypeOfBirth(goat.last_tob)}</td>
                      <td className="px-3 py-2 text-sm text-gray-600">{goat.total_kidding_count ?? 0}</td>
                      <td className={`px-3 py-2 text-sm sticky right-0 z-10 ${getStickyCellBgColor(goat.status)}`}>
                        <div className="flex items-center space-x-2">
                          {user?.role === 'admin' && (
                            <>
                              <Link
                                to={`/goat/master/${goat.id}/edit`}
                                className="text-blue-600 hover:text-blue-800"
                                title="Edit"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Link>
                              <button
                                onClick={() => handleDelete(goat.id, goat.tag_no)}
                                disabled={deletingId === goat.id}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                title="Delete"
                              >
                                {deletingId === goat.id ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <TrashIcon className="h-4 w-4" />
                                )}
                              </button>
                            </>
                          )}
                          {user?.role !== 'admin' && (
                            <span className="text-gray-400 text-xs">View only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">🐐</div>
            <p className="text-gray-500 mb-4">No goat records found</p>
            <Link
              to="/goat/master/new"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add First Goat
            </Link>
          </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && modalImageSrc && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeImageModal}
              className="absolute top-4 right-4 bg-white rounded-full p-2 hover:bg-gray-100 shadow-lg z-10 btn-touch"
              title="Close"
            >
              <XMarkIcon className="h-6 w-6 text-gray-600" />
            </button>
            <img
              src={modalImageSrc}
              alt="Goat"
              className="w-full h-auto rounded-lg shadow-2xl object-contain max-h-[90vh]"
            />
          </div>
        </div>
      )}

      {/* Image Upload Modal - Add Image Only */}
      {showImageUploadModal && uploadingGoatId && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeImageUploadModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                Add Image for {uploadingGoatTag}
              </h2>
              <button
                type="button"
                onClick={closeImageUploadModal}
                className="text-gray-400 hover:text-gray-600 btn-touch"
                title="Close"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              {/* Hidden file input */}
              <input
                id="goat-image-upload-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleImageChange}
                className="hidden"
              />

              {/* Image preview or placeholder */}
              <div className="flex justify-center">
                {imagePreview ? (
                  <div className="relative">
                    <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-gray-200">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={removeImagePreview}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 btn-touch"
                      title="Remove"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="goat-image-upload-input"
                    className="w-48 h-48 rounded-full border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 hover:bg-gray-100 hover:border-gray-400 transition-colors cursor-pointer"
                  >
                    <div className="text-center">
                      <CameraIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-sm text-gray-600">Click to select image</p>
                    </div>
                  </label>
                )}
              </div>

              <p className="text-xs text-gray-500 text-center">
                Max 10MB, will be compressed to 500KB
              </p>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                onClick={handleImageUpload}
                disabled={!imagePreview || uploadingImage}
                className="flex-1 btn-touch bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
              >
                {uploadingImage ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Uploading...</span>
                  </>
                ) : (
                  'Upload Image'
                )}
              </button>
              <button
                onClick={closeImageUploadModal}
                disabled={uploadingImage}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoatMasterPage;
